/**
 * VUKZ — Centralized Design Tokens
 * 
 * Import this instead of declaring const BRAND_GREEN = '...' per file.
 * Legacy aliases maintained for backward compatibility.
 * 
 * Usage:
 *   import { VUKZ, BRAND_PRIMARY } from '../theme/vukzTheme';
 *   <div style={{ color: VUKZ.primary }}>...</div>
 */

export const VUKZ = {
  primary:      '#111111',   // Near-black (VUKZ brand)
  primaryDark:  '#0b0b0c',   // Gradient dark end
  primaryLight: '#f3f4f6',   // Light gray background (replaces green tint)
  accent:       '#ffffff',   // White accent (clean SaaS)
  accentAlt:    '#6b7280',   // Cool gray accent (replaces gold/green)
  bg:           '#fafafa',   // Page background
  text:         '#111111',   // Primary text — near-black
  textLight:    '#6b7280',   // Secondary text
  border:       '#e5e7eb',   // Borders
  success:      '#16a34a',   // legado — não usar em UI nova (ver UI_PADRAO.md §1)
  warning:      '#f59e0b',   // legado — não usar em UI nova (ver UI_PADRAO.md §1)
  error:        '#dc2626',   // legado — não usar em UI nova; use alert.* abaixo
  /**
   * Paleta clínica — uso restrito (ver UI_PADRAO.md §1).
   * Vermelho dessaturado reservado a SINAIS CLÍNICOS INDIVIDUAIS críticos:
   * lesão ativa, fadiga ≥ 4, soreness ≥ 3, mood = 1, score < 0, alerta RED,
   * ACWR > 1.5. Agregados (médias do elenco, contagens) vão para cinza neutro.
   * Token único e centralizado — não recriar localmente em telas novas.
   */
  alert: {
    bg:     '#fef2f2',
    border: '#f3d1d1',
    text:   '#7f1d1d',
    dot:    '#a93333',
  },
};

/**
 * VUKZ UX Standard (congelado 31/07/2026 — ver discussão "uma tela, uma ação").
 * Princípios NÃO NEGOCIÁVEIS para telas novas ou redesenhadas:
 *  - corpo de texto 16px mínimo; números-chave 28–32px; nada abaixo de 14px
 *  - alvo de toque mínimo 48px de altura; ação principal ocupa a largura toda
 *  - concluir treino = 1 toque, sem modal de confirmação; RPE é segundo passo opcional
 *  - estado sempre visível sem clique: cor E ícone, nunca só cor
 *  - zero jargão na visão do atleta (sem "carga interna", "ACWR", "monotonia")
 *  - uma tela, uma ação principal; o resto é secundário e rolável
 */
export const UX = {
  type: {
    body:    '16px',   // corpo de texto mínimo
    small:   '14px',   // piso absoluto (labels, metadados)
    title:   '20px',   // títulos de seção/card
    keyNum:  '30px',   // números-chave (28–32px)
  },
  touch: {
    min:     48,       // px — altura mínima de qualquer alvo tocável
    primary: 56,       // px — botão de ação principal (largura total)
  },
};

/**
 * Estados de dia/atividade (visão do atleta) — sempre cor + ícone.
 * icon = nome lucide sugerido; nunca comunicar estado só por cor.
 */
export const DAY_STATE = {
  done:     { bg: '#eef2ee', text: '#2f5233', border: '#cddccf', icon: 'check' },
  pending:  { bg: '#f3f4f6', text: '#374151', border: '#e5e7eb', icon: 'circle' },
  modified: { bg: '#fef9ec', text: '#7c5a12', border: '#f0e2bd', icon: 'shuffle' },
  absent:   { bg: '#fef2f2', text: '#7f1d1d', border: '#f3d1d1', icon: 'x' },
  rest:     { bg: '#ffffff', text: '#9ca3af', border: '#e5e7eb', icon: 'minus' },
};

// Legacy aliases — backward compat (consumers still import these)
export const SUCRON = VUKZ;
export const BRAND_GREEN = VUKZ.primary;
export const BRAND_GOLD = VUKZ.accentAlt;
export const BRAND_GREEN_LIGHT = VUKZ.primaryLight;
export const BRAND_WHITE = VUKZ.accent;
export const BRAND_PRIMARY = VUKZ.primary;

// Gradient helper
export const SUCRON_GRADIENT = `linear-gradient(135deg, ${VUKZ.primary} 0%, ${VUKZ.primaryDark} 100%)`;
export const VUKZ_GRADIENT = SUCRON_GRADIENT;
